import React, { useState } from 'react';
import { Maximize2, ZoomIn, ZoomOut, RotateCcw, X, Info } from 'lucide-react';

interface ArchivalImageCardProps {
  src: string;
  alt: string;
  caption: string;
  filenameHint?: string;
  className?: string;
}

export const ArchivalImageCard: React.FC<ArchivalImageCardProps> = ({
  src,
  alt,
  caption,
  filenameHint,
  className = ''
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imgError, setImgError] = useState(false);

  // Normalize image path if passed with 'public/' prefix
  const resolvedSrc = src.startsWith('public/') ? `/${src.replace(/^public\//, '')}` : src;

  // Fallback image URL if the image path fails
  const fallbackSrc = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=1200';

  const handleZoomIn = (e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomLevel(prev => Math.min(prev + 0.3, 3));
  };

  const handleZoomOut = (e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomLevel(prev => Math.max(prev - 0.3, 0.8));
  };

  const handleResetZoom = (e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomLevel(1);
  };

  return (
    <>
      {/* Archival Image Card Container */}
      <figure className={`my-6 sm:my-8 group rounded-2xl overflow-hidden bg-[#f3ede0] border-2 border-[#d8ceb9] shadow-md hover:shadow-xl transition-all duration-300 w-full ${className}`}>
        
        {/* Image Container with Zoom Prompt Overlay */}
        <div 
          onClick={() => setIsLightboxOpen(true)}
          className="relative cursor-pointer overflow-hidden bg-[#2c221e]/5 min-h-[200px] sm:min-h-[280px] md:min-h-[340px] flex items-center justify-center group"
          title="Click to open high-resolution archival lightbox"
        >
          <img
            src={imgError ? fallbackSrc : resolvedSrc}
            alt={alt}
            referrerPolicy="no-referrer"
            onError={() => setImgError(true)}
            className="w-full h-auto max-h-[520px] object-cover sm:object-contain transition-transform duration-500 group-hover:scale-[1.02]"
            loading="lazy"
          />

          {/* Hover Overlay with Lightbox Trigger */}
          <div className="absolute inset-0 bg-[#2c221e]/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2 text-white p-4">
            <span className="bg-[#4a3525]/90 text-amber-100 px-4 py-2 rounded-xl text-xs font-semibold backdrop-blur-xs flex items-center gap-2 border border-amber-200/30 shadow-lg">
              <Maximize2 className="w-4 h-4 text-amber-300" />
              <span>Inspect Archival Detail (Click to Zoom)</span>
            </span>
          </div>

          {/* Top Corner Archival Stamp */}
          <div className="absolute top-3 left-3 bg-[#4a3525]/85 text-[#fbf9f4] px-2.5 py-1 rounded-md text-[10px] uppercase font-mono tracking-widest border border-[#d8ceb9]/40 shadow-xs pointer-events-none">
            📸 Archival Image
          </div>
        </div>

        {/* Formatted Caption Bar */}
        <figcaption className="bg-[#e8dec9] px-4 sm:px-5 py-3 sm:py-3.5 border-t border-[#d8ceb9] flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-[#2c221e]">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <Info className="w-4 h-4 text-[#7a5b3a] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-serif text-xs sm:text-sm font-semibold text-[#2c221e] leading-snug break-words">
                {caption}
              </p>
              {filenameHint && (
                <p className="text-[10px] sm:text-[11px] font-mono text-[#7a5b3a] mt-0.5 opacity-80 truncate">
                  Asset: {filenameHint}
                </p>
              )}
            </div>
          </div>

          <button
            onClick={() => setIsLightboxOpen(true)}
            className="min-h-[44px] min-w-[44px] text-xs font-semibold text-[#7a5b3a] hover:text-[#2c221e] flex items-center justify-center gap-1.5 shrink-0 self-end sm:self-auto bg-[#f8f5ee] px-3 py-2 rounded-xl border border-[#d8ceb9] transition hover:bg-white shadow-xs cursor-pointer"
          >
            <ZoomIn className="w-4 h-4 text-[#7a5b3a]" />
            <span>Zoom</span>
          </button>
        </figcaption>
      </figure>

      {/* Lightbox Modal */}
      {isLightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-[#1c1613]/95 backdrop-blur-md flex flex-col justify-between p-3 sm:p-6 md:p-8 animate-fade-in text-[#fbf9f4] overflow-y-auto"
          onClick={() => setIsLightboxOpen(false)}
        >
          {/* Top Bar Controls */}
          <div className="flex flex-wrap items-center justify-between border-b border-[#4a3525] pb-3 sm:pb-4 gap-3" onClick={e => e.stopPropagation()}>
            <div className="min-w-0 flex-1 pr-2">
              <h4 className="font-serif text-sm sm:text-base md:text-lg font-bold text-amber-100 flex items-center gap-2 truncate">
                <span>Historical Photograph Inspection</span>
              </h4>
              <p className="text-[11px] sm:text-xs text-amber-200/70 truncate">{caption}</p>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={handleZoomIn}
                className="min-h-[44px] min-w-[44px] px-3 py-2 rounded-xl bg-[#4a3525] hover:bg-[#634833] text-amber-100 transition text-xs font-semibold flex items-center justify-center gap-1 border border-amber-200/20 cursor-pointer"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4 text-amber-300" />
                <span className="hidden sm:inline">Zoom In</span>
              </button>
              <button
                onClick={handleZoomOut}
                className="min-h-[44px] min-w-[44px] px-3 py-2 rounded-xl bg-[#4a3525] hover:bg-[#634833] text-amber-100 transition text-xs font-semibold flex items-center justify-center gap-1 border border-amber-200/20 cursor-pointer"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4 text-amber-300" />
                <span className="hidden sm:inline">Zoom Out</span>
              </button>
              <button
                onClick={handleResetZoom}
                className="min-h-[44px] min-w-[44px] px-3 py-2 rounded-xl bg-[#4a3525] hover:bg-[#634833] text-amber-100 transition text-xs font-semibold flex items-center justify-center gap-1 border border-amber-200/20 cursor-pointer"
                title="Reset Zoom"
              >
                <RotateCcw className="w-4 h-4 text-amber-300" />
                <span className="hidden sm:inline">100%</span>
              </button>

              <button
                onClick={() => setIsLightboxOpen(false)}
                className="min-h-[44px] min-w-[44px] px-3 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white transition font-bold ml-1 flex items-center justify-center gap-1 shadow-md cursor-pointer"
                title="Close Lightbox"
              >
                <X className="w-5 h-5" />
                <span className="hidden sm:inline">Close</span>
              </button>
            </div>
          </div>

          {/* Main Zoomable Image Stage */}
          <div 
            className="flex-1 my-3 sm:my-4 flex items-center justify-center overflow-auto relative cursor-grab active:cursor-grabbing p-1 sm:p-2 min-h-[300px]"
            onClick={e => e.stopPropagation()}
          >
            <img
              src={imgError ? fallbackSrc : resolvedSrc}
              alt={alt}
              referrerPolicy="no-referrer"
              style={{ transform: `scale(${zoomLevel})` }}
              className="max-h-[65vh] sm:max-h-[75vh] max-w-full object-contain transition-transform duration-200 shadow-2xl rounded-xl border-2 border-amber-200/30"
            />
          </div>

          {/* Bottom Caption Bar */}
          <div className="bg-[#2c221e] p-3 sm:p-4 rounded-xl border border-amber-200/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-amber-100/90" onClick={e => e.stopPropagation()}>
            <div className="flex items-start sm:items-center gap-2 min-w-0">
              <span className="font-bold text-amber-300 shrink-0">Caption:</span>
              <span className="break-words">{caption}</span>
            </div>
            <div className="text-[10px] sm:text-[11px] font-mono text-amber-200/60 shrink-0">
              Negus Historical Archive
            </div>
          </div>
        </div>
      )}
    </>
  );
};

